import { SimpleGrid } from '@mantine/core';
import { TenantFilter } from 'components/TenantFilter';
import { useState } from 'react';
import { partition, prop, sortBy } from 'remeda';
import { useLoadGroupOverview } from 'shared/hooks/use-load-group-overview';
import type { Profile } from 'shared/hooks/use-load-profile';
import { AllClearStrip } from './AllClearStrip';
import { TenantCard } from './TenantCard';
import { TenantCardError } from './TenantCardError';
import { TenantCardPlaceholder } from './TenantCardPlaceholder';
import { hasSomethingOpen } from './tenant';

type TenantCardsProps = {
  tenants: ReadonlyArray<Profile['accessibleTenants'][number]>;
};

export const TenantCards = ({ tenants }: TenantCardsProps) => {
  const [selectedSubdomains, setSelectedSubdomains] = useState<string[]>([]);
  const { groupOverview, failedSubdomains, isGroupOverviewLoading } = useLoadGroupOverview({
    subdomains: tenants.map(({ subdomain }) => subdomain),
    isEnabled: true,
  });

  const isChosen = (subdomain: string) =>
    selectedSubdomains.length === 0 || selectedSubdomains.includes(subdomain);

  const profileTenantOf = (subdomain: string) =>
    tenants.find(tenant => tenant.subdomain === subdomain);

  const companyNameOf = (subdomain: string) => profileTenantOf(subdomain)?.companyName ?? subdomain;

  const [openTenants, allClearTenants] = partition(
    sortBy(
      (groupOverview?.tenants ?? []).filter(({ subdomain }) => isChosen(subdomain)),
      [prop('debtorsToHandle'), 'desc'],
    ),
    hasSomethingOpen,
  );

  const placeholderCards = tenants.map(({ subdomain, companyName }) => (
    <TenantCardPlaceholder key={subdomain} companyName={companyName} />
  ));

  const settledCards = [
    ...openTenants.map(tenant => (
      <TenantCard
        key={tenant.subdomain}
        tenant={tenant}
        companyName={companyNameOf(tenant.subdomain)}
        tenantUrl={profileTenantOf(tenant.subdomain)?.url ?? ''}
      />
    )),
    ...failedSubdomains
      .filter(isChosen)
      .map(subdomain => <TenantCardError key={subdomain} companyName={companyNameOf(subdomain)} />),
  ];

  return (
    <>
      <TenantFilter
        tenants={tenants}
        selectedSubdomains={selectedSubdomains}
        onChange={setSelectedSubdomains}
        mb="lg"
        maw={420}
      />

      <SimpleGrid
        cols={{ base: 1, sm: 2, lg: 3 }}
        spacing="lg"
        component="ul"
        p={0}
        style={{ listStyle: 'none' }}
      >
        {isGroupOverviewLoading ? placeholderCards : settledCards}
      </SimpleGrid>

      <AllClearStrip
        companyNames={allClearTenants.map(({ subdomain }) => companyNameOf(subdomain))}
      />
    </>
  );
};
